import {
  UseMutationOptions,
  useMutation,
  useQueryClient,
  QueryKey,
} from '@tanstack/react-query';

import APIError from 'types/api_error';
import { Tag } from 'types/tag';

import { APIObject } from 'services/api';
import {
  updateQueriesData,
  UpdateQueriesDataContext,
} from 'services/update_queries_data';

import { useAPI } from 'hooks/api/useAPI';
import { TaggableEntityObject } from 'hooks/useEntities';

export type UseDeleteEntityTagsOptions = {
  entity: keyof APIObject;
  listQueryKey?: QueryKey;
  singleQueryKey?: QueryKey;
};

export type UseDeleteEntityTagsParameters<T extends TaggableEntityObject> = {
  id: T['id'];
  tagIds: Tag['id'][];
};

export function useDeleteEntityTags<T extends TaggableEntityObject>(
  {
    entity,
    listQueryKey = [entity],
    singleQueryKey,
  }: UseDeleteEntityTagsOptions,
  {
    onMutate,
    onError,
    onSettled,
    ...options
  }: UseMutationOptions<
    Tag['id'][],
    APIError,
    UseDeleteEntityTagsParameters<T>,
    UpdateQueriesDataContext<T>
  > = {}
) {
  const api = useAPI();
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, tagIds }: UseDeleteEntityTagsParameters<T>) => {
      const entityAPI = api[entity];

      if (!id) {
        return Promise.reject(`Missing id`);
      }

      if (
        !entityAPI ||
        typeof entityAPI !== 'object' ||
        !('removeTags' in entityAPI) ||
        !entityAPI.removeTags
      ) {
        return Promise.reject(`Invalid entity type "${entity}".`);
      }

      return entityAPI.removeTags(id, tagIds);
    },
    {
      ...options,
      async onMutate({ id, tagIds }) {
        await queryClient.cancelQueries(listQueryKey);
        await queryClient.cancelQueries(singleQueryKey);
        onMutate?.({ id, tagIds });

        return updateQueriesData<T>({
          queryClient,
          listQueryKey,
          singleQueryKey,
          ids: [id],
          updateData(entity) {
            entity.tags = entity.tags.filter(
              (tagId) => !tagIds.includes(tagId)
            );
            return entity;
          },
        });
      },
      onError(error, updatedStream, context) {
        onError?.(error, updatedStream, context);

        context?.previousLists?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
        context?.previousSingles?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
      },
      onSettled(...args) {
        onSettled?.(...args);
        queryClient.invalidateQueries(listQueryKey);
        queryClient.invalidateQueries(singleQueryKey);
      },
    }
  );
}
